import { PurchaseRequestFlowStepper } from "@fero/components";
import { useGetProducibleProductsQuery } from "@fero/hooks";
import { PortalPageContent, PortalPageContentHeader } from "@fero/layouts";
import type {
  FormStorageLocProductSale,
  PurchaseRequestFlowState,
  StorageLocationProductSale,
} from "@fero/pages";
import { type SelectedProduct, useTradeSalesStore } from "@fero/stores";
import { AppRoutes } from "@fero/utils";
import { Alert, Grid, Stack, Typography } from "@mui/material";
import type {
  NominatedPurchaserDetailsView,
  NominatedPurchaserIndexItemView,
} from "adl-gen/ferovinum/app/api";
import type {
  ProductId,
  PurchaseRequestSalePriceType,
} from "adl-gen/ferovinum/app/db";
import { useFormik } from "formik";
import { useCallback, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { ensure } from "utils/null-and-undefined-utils";
import { isValidNumber } from "utils/ts-utils";
import { PurchaserSelectorCard } from "./components";
import { getNewInitialValuesForProductsForm } from "./components/initial-form-values";
import { SetupFormSection } from "./components/new-form-section";
import {
  extractNonFinishedProducts,
  type SetupPurchaserRequestFormValues,
} from "./organisation-purchase-request-setup-page";

export interface SelectedProductsFormValues {
  selectedProducts: SelectedProduct[];
  bondedSale: boolean;
  dealDiscountAmount: string;
  dealDiscountPercentage: string;
  salePriceType: PurchaseRequestSalePriceType;
  poFile?: File;
}

const OrganisationPurchaseRequestSetupHeader = ({
  purchaserName,
}: {
  purchaserName: string;
}) => {
  const { options } = useTradeSalesStore();
  return (
    <PortalPageContentHeader
      variant="split"
      title={
        <Stack>
          <Typography variant={"h4"}>
            {options.isEditMode ? "Edit Trade Sale" : "New Trade Sale"}
          </Typography>
          <Typography>from {purchaserName}</Typography>
        </Stack>
      }
      right={<PurchaseRequestFlowStepper activeStep={0} />}
    />
  );
};

const mapSelectedProductsFormValuesToSetupPurchaserRequestFormValues = (
  details: NominatedPurchaserDetailsView,
  values: SelectedProductsFormValues,
): SetupPurchaserRequestFormValues => {
  const totalAmount = values.selectedProducts.reduce(
    (sum, p) => sum + p.subtotal,
    0,
  );
  const dealDiscountAmount = values.dealDiscountPercentage
    ? (totalAmount * (Number(values.dealDiscountPercentage) / 100)).toString()
    : values.dealDiscountAmount;
  return {
    ...values,
    purchaser: details,
    dealDiscountAmount,
    products: values.selectedProducts.map((p) => {
      // converting % discount to actual value base
      const productDiscountValue = p.lineItemDiscount
        ? (p.salePrice * (p.lineItemDiscount / 100)).toString()
        : undefined;
      return {
        product: p.summary,
        unitPrice: p.salePrice ? p.salePrice.toString() : "0",
        priceDiscount: productDiscountValue,
        lineItemDiscount: p.lineItemDiscount,
        paidUnits: p.quantity ? p.quantity.toString() : "0",
        freeUnits: p.freeUnits ? p.freeUnits.toString() : undefined,
        totalAvailable: p.available,
        totalProducible: p.producible,
        attributeSelection: p.attributeSelection,
      };
    }),
  };
};

export const NewOrganisationPurchaseRequestSetupPage = () => {
  const location = useLocation();
  const history = useHistory();
  const purchaseRequestFlowState = location.state as
    | PurchaseRequestFlowState<NominatedPurchaserIndexItemView>
    | undefined;
  const { options, updateOptions } = useTradeSalesStore();
  const [selectedPurchaser, setSelectedPurchaser] =
    useState<NominatedPurchaserDetailsView>();
  const initialValues = getNewInitialValuesForProductsForm(
    purchaseRequestFlowState?.setup,
  );
  const { data: producibleProducts, isLoading: isProducibleDataLoading } =
    useGetProducibleProductsQuery();
  const producibles = (producibleProducts ?? [])
    .flatMap((x) => x.value)
    .reduce(
      (map, x) =>
        map.set(
          x.productWithId.id,
          x.quantityProducible.value.toString() ?? "-",
        ),
      new Map<ProductId, string>(),
    );

  useEffect(() => {
    updateOptions({ nominatedPurchaser: selectedPurchaser });
  }, [selectedPurchaser, updateOptions]);

  // NB: This is taken verbose from the old organisation-purchase-request-setup-page.tsx so it
  // integrates nicely with the rest of the flow when we're done with product selection
  const onNext = useCallback(
    async (values: SetupPurchaserRequestFormValues) => {
      const filterSelectedProducts = (
        products: FormStorageLocProductSale[],
      ): StorageLocationProductSale[] => {
        return products
          .filter(
            (p) =>
              (isValidNumber(p.paidUnits) && isValidNumber(p.unitPrice)) ||
              isValidNumber(p.freeUnits),
          )
          .map((p) => ({
            ...p,
            paidUnits: p.paidUnits ? Number(p.paidUnits) : 0,
            freeUnits:
              p.freeUnits !== undefined ? Number(p.freeUnits) : undefined,
            unitPrice: p.unitPrice ? Number(p.unitPrice) : 0,
            priceDiscount:
              p.priceDiscount !== undefined
                ? Number(p.priceDiscount)
                : undefined,
          }));
      };

      const storageLocation = options.storageLocation;

      const newState: PurchaseRequestFlowState = {
        ...purchaseRequestFlowState,
        // Note: For now reset the production plan if there was any
        // too much hassle to check if the previous plan is still compatible or not if they change the sources
        productionPlan: undefined,
        setup: {
          storageLocation: ensure(storageLocation),
          purchaser: ensure(values.purchaser),
          products: filterSelectedProducts(values.products),
          dealDiscountAmount: values.dealDiscountAmount,
          bondedSale: values.bondedSale,
          // Sale price type is only relevant for out of bond sales. For in bond sales, the price is always excluding duty
          salePriceType: values.bondedSale
            ? "exDutyAndVat"
            : values.salePriceType,
          poFile: values.poFile,
        },
      };

      if (
        extractNonFinishedProducts(newState.setup?.products ?? []).length > 0
      ) {
        history.push(
          AppRoutes.OrganisationPurchaseRequestProductionPlan,
          newState,
        );
      } else {
        // reset production plan as it might not be applicable anymore
        newState.productionPlan = undefined;
        history.push(
          AppRoutes.OrganisationPurchaseRequestDeliveryOptions,
          newState,
        );
      }
    },
    [history, options.storageLocation, purchaseRequestFlowState],
  );

  const onSubmit = useCallback(
    (values: SelectedProductsFormValues) => {
      if (selectedPurchaser == null) return;
      onNext(
        mapSelectedProductsFormValuesToSetupPurchaserRequestFormValues(
          selectedPurchaser,
          values,
        ),
      );
    },
    [selectedPurchaser, onNext],
  );

  const form = useFormik<SelectedProductsFormValues>({
    initialValues: initialValues,
    validateOnMount: true,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit,
  });

  const resetFormProductsToInitial = useCallback(() => {
    form.setFieldValue(
      "selectedProducts",
      initialValues.selectedProducts ?? [],
    );
    form.setErrors({});
  }, [form, initialValues.selectedProducts]);

  return (
    <PortalPageContent
      header={
        <OrganisationPurchaseRequestSetupHeader
          purchaserName={options.nominatedPurchaser?.name ?? "Purchaser"}
        />
      }
    >
      <Grid container spacing={5}>
        <Grid item xs={12}>
          <PurchaserSelectorCard
            selectedPurchaser={
              selectedPurchaser ?? purchaseRequestFlowState?.setup?.purchaser
            }
            onChange={(purchaser) => {
              if (!purchaser) {
                return;
              }
              setSelectedPurchaser(purchaser);
              resetFormProductsToInitial();
            }}
          />
        </Grid>
        {options.nominatedPurchaser != null &&
        options.nominatedPurchaser.nominatedPurchaserId ===
          selectedPurchaser?.nominatedPurchaserId ? (
          <SetupFormSection
            form={form}
            poUploadEnabled={
              options.nominatedPurchaser?.poUploadEnabled ?? false
            }
            savedPurchaserDiscountPercentage={
              options.nominatedPurchaser?.savedDiscountPct ?? undefined
            }
            resetFormProductsToInitial={resetFormProductsToInitial}
            producibles={producibles}
            isProducibleDataLoading={isProducibleDataLoading}
          />
        ) : (
          <Alert sx={{ marginLeft: 5, marginTop: 2 }} severity="info">
            Please select a purchaser.
          </Alert>
        )}
      </Grid>
    </PortalPageContent>
  );
};
